import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { IpRestrictionGuard } from "@/components/IpRestrictionGuard";
import { usePageTracking } from "@/lib/ux-analytics";

// Eager imports - core pages that users access most frequently
import { Login } from "@/pages/login";
import { AppShell } from "@/components/layout/AppShell";
import { Dashboard } from "@/pages/dashboard";
import { Domains } from "@/pages/domains";
import { DomainDetail } from "@/pages/domains/[id]";
import { Sources } from "@/pages/sources";
// Sources Spine — per-Source detail page (Phase 1 PR 8). Lazy because
// it's reached via per-row navigation, not the main nav; doesn't need
// to be in the eager bundle.
const SourceDetail = lazy(() =>
  import("@/pages/sources/[id]").then((m) => ({ default: m.SourceDetail }))
);
const CompositeSourceDetail = lazy(() =>
  import("@/pages/sources/composite").then((m) => ({
    default: m.CompositeSourceDetail,
  }))
);
import { Alerts } from "@/pages/alerts";
import { AuthCallback } from "@/pages/auth/callback"; // Eager load to debug auth issue

// Lazy imports - secondary pages loaded on demand
// AuthCallback moved to eager imports above for debugging
const AuthError = lazy(() =>
  import("@/pages/auth/error").then((m) => ({ default: m.AuthError }))
);
const InviteAccept = lazy(() =>
  import("@/pages/auth/invite").then((m) => ({ default: m.InviteAccept }))
);
const GetStarted = lazy(() =>
  import("@/pages/get-started").then((m) => ({ default: m.GetStarted }))
);
const StartFree = lazy(() =>
  import("@/pages/start-free").then((m) => ({ default: m.StartFree }))
);
const Onboarding = lazy(() =>
  import("@/pages/onboarding").then((m) => ({ default: m.Onboarding }))
);
const Checkout = lazy(() =>
  import("@/pages/checkout").then((m) => ({ default: m.Checkout }))
);
const ShareDashboard = lazy(() =>
  import("@/pages/share").then((m) => ({ default: m.ShareDashboard }))
);
const ShareLinksPage = lazy(() =>
  import("@/pages/share-links").then((m) => ({ default: m.ShareLinksPage }))
);
const Status = lazy(() =>
  import("@/pages/status").then((m) => ({ default: m.Status }))
);
// Design mockups: six screens of invented metrics rendered in real product
// chrome. Useful for design review, indistinguishable from live data to
// anyone who lands on it, and it was routed in the PUBLIC block with no auth
// guard and no environment check — so it was reachable by URL on the
// customer-facing domain.
//
// Declared behind a build-time constant rather than a runtime check so the
// condition folds to `false` in a production build and Rollup can drop the
// dynamic import entirely. A runtime guard would still ship the chunk, and a
// chunk on the server is still fetchable by anyone who guesses its name.
const MockupsPage = import.meta.env.DEV
  ? lazy(() =>
      import("@/pages/mockups").then((m) => ({ default: m.MockupsPage }))
    )
  : null;
// Phase 1 PR 13 — the /enforcement page is back as the spine-driven
// blast-radius simulator (replaces the legacy readiness-dial page).
const Enforcement = lazy(() =>
  import("@/pages/enforcement").then((m) => ({ default: m.Enforcement }))
);
const Reports = lazy(() =>
  import("@/pages/reports").then((m) => ({ default: m.Reports }))
);
const Help = lazy(() =>
  import("@/pages/help").then((m) => ({ default: m.Help }))
);
const Audit = lazy(() =>
  import("@/pages/audit").then((m) => ({ default: m.Audit }))
);
const Settings = lazy(() =>
  import("@/pages/settings").then((m) => ({ default: m.Settings }))
);

// MSP (Managed Service Provider) pages
const MspDashboard = lazy(() =>
  import("@/pages/msp").then((m) => ({ default: m.MspDashboard }))
);
const NewMspClient = lazy(() =>
  import("@/pages/msp/clients/new").then((m) => ({ default: m.NewMspClient }))
);
const MspClientDetail = lazy(() =>
  import("@/pages/msp/clients/[id]").then((m) => ({ default: m.MspClientDetail }))
);
const MspBilling = lazy(() =>
  import("@/pages/msp/billing").then((m) => ({ default: m.MspBilling }))
);
const MspBranding = lazy(() =>
  import("@/pages/msp/branding").then((m) => ({ default: m.MspBranding }))
);
const MspEmailDomains = lazy(() =>
  import("@/pages/msp/email-domains").then((m) => ({ default: m.MspEmailDomains }))
);
// White-label pages
const MspWhiteLabelHub = lazy(() =>
  import("@/pages/msp/white-label").then((m) => ({ default: m.MspWhiteLabel }))
);
const MspWhiteLabelEmailDomains = lazy(() =>
  import("@/pages/msp/white-label/email-domains").then((m) => ({
    default: m.MspWhiteLabelEmailDomains,
  }))
);
const MspWhiteLabelAppDomains = lazy(() =>
  import("@/pages/msp/white-label/app-domains").then((m) => ({ default: m.MspAppDomains }))
);
const MspTeamPage = lazy(() =>
  import("@/pages/msp/team").then((m) => ({ default: m.MspTeamPage }))
);
const MspTeamMemberPage = lazy(() =>
  import("@/pages/msp/team/[userId]").then((m) => ({ default: m.MspTeamMemberPage }))
);
const MspClientsPage = lazy(() =>
  import("@/pages/msp/clients").then((m) => ({ default: m.MspClientsPage }))
);

// Development/Testing pages
const MspTestingPanel = lazy(() =>
  import("@/pages/dev/msp-testing").then((m) => ({ default: m.MspTestingPanel }))
);

// Loading spinner component for Suspense fallback
function PageLoader() {
  return (
    <div className="h-full min-h-[400px] flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
    </div>
  );
}

// Full-page loader for routes outside AppShell
function FullPageLoader() {
  return (
    <div className="h-screen flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  // Track page views for authenticated users
  usePageTracking();

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    // Redirect to login page for returning users
    return <Navigate to="/login" replace />;
  }

  // Wrap with IP restriction guard to check on every navigation
  return <IpRestrictionGuard>{children}</IpRestrictionGuard>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<FullPageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/start-free" element={<StartFree />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/share/:token" element={<ShareDashboard />} />
          {/* APEX A2 (PR 46) — per-recipient scoped link route. The
              token in the URL is the recipient_token; ShareDashboard
              detects this branch via the useParams shape. */}
          <Route path="/share/r/:recipientToken" element={<ShareDashboard />} />
          <Route path="/apex/:token" element={<ShareDashboard />} />
          <Route path="/status" element={<Status />} />
          {MockupsPage && (
            <Route path="/mockups" element={<MockupsPage />} />
          )}

          {/* Auth routes */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/error" element={<AuthError />} />
          <Route path="/auth/invite/:token" element={<InviteAccept />} />

          {/* Protected app routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        {/* Sources Spine — blast-radius simulator (Phase 1 PR 13). */}
                        <Route path="/enforcement" element={<Enforcement />} />
                        <Route path="/domains" element={<Domains />} />
                        <Route path="/domains/:id" element={<DomainDetail />} />
                        <Route path="/sources" element={<Sources />} />
                        {/* PR 68 — composite source detail. Must come
                            BEFORE /sources/:id so the literal segment
                            wins; otherwise React Router treats
                            "composite" as the :id and routes to the
                            per-source page (which 404s on the
                            non-existent id). */}
                        <Route
                          path="/sources/composite/:id"
                          element={<CompositeSourceDetail />}
                        />
                        {/* Sources Spine per-Source detail (Phase 1 PR 8). */}
                        <Route path="/sources/:id" element={<SourceDetail />} />
                        <Route path="/alerts" element={<Alerts />} />
                        {/* Legacy route. The page it used to render was
                            deleted in the dead-surfaces batch; remediation
                            tasks are `kind: "task"` rows in the unified
                            action_items list. Kept as a redirect because the
                            path is in customer bookmarks and in older email. */}
                        <Route path="/remediation" element={<Navigate to="/alerts?kind=task" replace />} />
                        <Route path="/reports" element={<Reports />} />
                        {/* APEX 46.6 — org-wide APEX share-links manager. */}
                        <Route path="/share-links" element={<ShareLinksPage />} />
                        <Route path="/help" element={<Help />} />
                        <Route path="/audit" element={<Audit />} />
                        {/* Legacy route. The standalone Enterprise BIMI
                            manager was deleted in the dead-surfaces batch —
                            658 lines behind a commented-out import. BIMI now
                            lives on the domain detail BIMI tab, and
                            `?filter=bimi` is read by the domains page. */}
                        <Route path="/bimi" element={<Navigate to="/domains?filter=bimi" replace />} />
                        <Route path="/settings/*" element={<Settings />} />
                        {/* MSP Routes */}
                        <Route path="/msp" element={<MspDashboard />} />
                        <Route path="/msp/clients" element={<MspClientsPage />} />
                        <Route path="/msp/clients/new" element={<NewMspClient />} />
                        <Route path="/msp/clients/:id" element={<MspClientDetail />} />
                        <Route path="/msp/billing" element={<MspBilling />} />
                        <Route path="/msp/branding" element={<MspBranding />} />
                        <Route path="/msp/email-domains" element={<MspEmailDomains />} />
                        <Route path="/msp/white-label" element={<MspWhiteLabelHub />} />
                        <Route path="/msp/white-label/email-domains" element={<MspWhiteLabelEmailDomains />} />
                        <Route path="/msp/white-label/app-domains" element={<MspWhiteLabelAppDomains />} />
                        <Route path="/msp/team" element={<MspTeamPage />} />
                        <Route path="/msp/team/:userId" element={<MspTeamMemberPage />} />
                        {/* Development/Testing Routes */}
                        <Route path="/dev/msp-testing" element={<MspTestingPanel />} />
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                </AppShell>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
